import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AuroraSystemConfig, AuroraBomItem, SystemProposal } from '../../../utils/aurora-mapper.utils';
import {
  DISPLAY_SYSTEM_COUNT,
  SYSTEM_COLUMNS,
  COMPONENT_LABELS,
  CATEGORY_ROWS,
  ITEM_CATEGORIES,
  ItemCategory,
} from '../../../constants';

interface AuroraMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  onSkipToStructures?: () => void;
  onConfirm: (config: AuroraSystemConfig) => void;
  initialConfig: AuroraSystemConfig;
  embedded?: boolean;
}

interface FlatItem {
  bomItem: AuroraBomItem;
  category: ItemCategory;
  systemIdx: number;
}

function flattenConfig(config: AuroraSystemConfig): FlatItem[] {
  const items: FlatItem[] = [];
  config.systems.forEach((sys, sysIdx) => {
    ITEM_CATEGORIES.forEach(cat => {
      sys[cat].forEach(bomItem => {
        items.push({ bomItem, category: cat, systemIdx: sysIdx });
      });
    });
  });
  return items;
}

function rebuildFromFlat(items: FlatItem[], production: number | null): AuroraSystemConfig {
  const systems: SystemProposal[] = Array.from({ length: DISPLAY_SYSTEM_COUNT }, () => ({
    modules: [], inverters: [], microinverters: [], optimizers: [], combiners: [],
  }));
  items.forEach(item => {
    if (item.systemIdx < DISPLAY_SYSTEM_COUNT) {
      systems[item.systemIdx][item.category].push(item.bomItem);
    }
  });
  const systemCount = systems.filter(s =>
    s.modules.length > 0 || s.inverters.length > 0 || s.microinverters.length > 0 ||
    s.optimizers.length > 0 || s.combiners.length > 0
  ).length || 1;
  return { systemCount, systems, production };
}

const ItemCard: React.FC<{
  item: FlatItem;
  onMoveToSystem: (newIdx: number) => void;
}> = ({ item, onMoveToSystem }) => {
  const meta = COMPONENT_LABELS[item.category] || COMPONENT_LABELS.modules;

  return (
    <div className={`flex items-center gap-2 px-2.5 py-2 3xl:px-3 3xl:py-2.5 rounded-md border ${meta.color} transition-all duration-150`}>
      <svg className="w-3.5 h-3.5 3xl:w-4 3xl:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d={meta.icon} />
      </svg>
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="text-[11px] 3xl:text-xs font-medium truncate">{item.bomItem.name}</div>
        <div className="text-[10px] 3xl:text-[11px] opacity-70 truncate">{item.bomItem.manufacturer_name} &middot; Qty: {item.bomItem.quantity}</div>
      </div>
      <select
        value={item.systemIdx}
        onChange={(e) => onMoveToSystem(Number(e.target.value))}
        className="text-[10px] 3xl:text-[11px] bg-white/80 border border-current/20 rounded px-1 py-0.5 cursor-pointer focus:outline-none flex-shrink-0"
      >
        {Array.from({ length: DISPLAY_SYSTEM_COUNT }, (_, i) => (
          <option key={i} value={i}>S{i + 1}</option>
        ))}
      </select>
    </div>
  );
};

export const AuroraMappingModal: React.FC<AuroraMappingModalProps> = ({
  isOpen,
  onClose,
  onBack,
  onSkipToStructures,
  onConfirm,
  initialConfig,
  embedded = false,
}) => {
  const [flatItems, setFlatItems] = useState<FlatItem[]>(() => flattenConfig(initialConfig));

  React.useEffect(() => {
    if (isOpen) {
      setFlatItems(flattenConfig(initialConfig));
    }
  }, [initialConfig, isOpen]);

  const handleMoveItem = useCallback((itemIdx: number, newSystemIdx: number) => {
    setFlatItems(prev => prev.map((item, i) =>
      i === itemIdx ? { ...item, systemIdx: newSystemIdx } : item
    ));
  }, []);

  const handleConfirm = useCallback(() => {
    const config = rebuildFromFlat(flatItems, initialConfig.production);
    onConfirm(config);
  }, [flatItems, initialConfig.production, onConfirm]);

  if (!isOpen) return null;

  const { production } = initialConfig;

  // Filter to only category rows that have items
  const activeRows = CATEGORY_ROWS.filter(row =>
    flatItems.some(item => row.categories.includes(item.category))
  );

  const content = (
    <>
      {/* Header */}
      <div className="px-5 pt-3 pb-3 3xl:px-6 3xl:pt-4 3xl:pb-4 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 3xl:w-10 3xl:h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm">
              <svg className="w-5 h-5 3xl:w-6 3xl:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm 3xl:text-base font-semibold text-slate-800">System Configuration</h3>
              <p className="text-[11px] 3xl:text-xs text-slate-400">Review and reassign items between systems</p>
            </div>
          </div>
          {production != null && (
            <div className="text-right">
              <div className="text-[10px] 3xl:text-[11px] uppercase tracking-wider font-semibold text-slate-400">Production</div>
              <div className="text-sm 3xl:text-base font-mono font-semibold text-emerald-600">{Math.round(production).toLocaleString()} kWh</div>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 px-5 py-4 3xl:px-6 3xl:py-5 overflow-y-auto overflow-x-hidden">
        <div className="flex gap-3 3xl:gap-4 min-w-0">
          {/* Left: category labels */}
          <div className="w-24 3xl:w-28 flex-shrink-0 pt-10 3xl:pt-12">
            {activeRows.map(row => (
              <div key={row.key} className="h-[80px] 3xl:h-[92px] flex items-center">
                <span className="text-[10px] 3xl:text-[11px] font-bold uppercase tracking-wider text-slate-400 leading-tight">{row.label}</span>
              </div>
            ))}
          </div>

          {/* 3 system columns */}
          {SYSTEM_COLUMNS.map((col, sysIdx) => {
            const itemCount = flatItems.filter(item => item.systemIdx === sysIdx).length;
            return (
            <div key={sysIdx} className={`flex-1 min-w-0 rounded-xl border ${col.border} ${col.bg} overflow-hidden`}>
              {/* Column header */}
              <div className={`px-3 py-2.5 3xl:px-4 3xl:py-3 ${col.headerBg} flex items-center justify-center gap-2`}>
                <span className={`text-xs 3xl:text-sm font-bold ${col.headerText}`}>{col.label}</span>
                <span className={`text-[10px] 3xl:text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${col.badgeBg} ${col.badgeText}`}>
                  {itemCount}
                </span>
              </div>

              {/* Category cells */}
              {activeRows.map((row, rowIdx) => {
                const cellItems = flatItems
                  .map((item, globalIdx) => ({ item, globalIdx }))
                  .filter(({ item }) => item.systemIdx === sysIdx && row.categories.includes(item.category));

                return (
                  <div
                    key={row.key}
                    className={`h-[80px] 3xl:h-[92px] px-2.5 py-2 3xl:px-3 3xl:py-2.5 flex flex-col justify-center gap-1 ${rowIdx < activeRows.length - 1 ? `border-b ${col.border}` : ''}`}
                  >
                    {cellItems.length === 0 ? (
                      <div className={`text-center text-[10px] 3xl:text-[11px] ${col.empty}`}>---</div>
                    ) : (
                      cellItems.map(({ item, globalIdx }) => (
                        <ItemCard
                          key={`${item.bomItem.id}-${globalIdx}`}
                          item={item}
                          onMoveToSystem={(newIdx) => handleMoveItem(globalIdx, newIdx)}
                        />
                      ))
                    )}
                  </div>
                );
              })}
            </div>
          );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3.5 3xl:px-6 3xl:py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
        <p className="text-[10px] 3xl:text-[11px] text-slate-400">Use dropdowns to reassign items between systems</p>
        <div className="flex gap-2.5">
          <button
            onClick={onClose}
            className="px-3.5 py-1.5 3xl:px-4 3xl:py-2 text-xs 3xl:text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-3.5 py-1.5 3xl:px-4 3xl:py-2 text-xs 3xl:text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-lg shadow-sm shadow-emerald-500/20 transition-all duration-150"
          >
            Confirm Configuration
          </button>
        </div>
      </div>
    </>
  );

  if (embedded) return content;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl 3xl:max-w-6xl mx-auto shadow-2xl border border-gray-100 overflow-hidden flex flex-col h-[85vh]">
        {content}
      </div>
    </div>,
    document.body
  );
};
